import type { FC, ReactNode } from 'react'
import type { TileLook } from '@/components/landing/mockups/media-tile'

import {
    ArrowDownAZ,
    ArrowDownUp,
    ArrowLeft,
    ArrowUpZA,
    Bookmark,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Eye,
    EyeOff,
    Ghost,
    GripVertical,
    Layers,
    LayoutGrid,
    ListVideo,
    Pause,
    Pencil,
    Play,
    Plus,
    RotateCcw,
    RotateCw,
    Share2,
    Shuffle,
    SlidersHorizontal,
    Trash2,
    User,
    Volume2,
    VolumeX,
    X,
    ZoomIn
} from 'lucide-react'
import { cn } from '@/lib/utils'
import MediaTile from '@/components/landing/mockups/media-tile'

import {
    RangeTrack,
    ScreenButton,
    ScreenChip,
    SliderTrack
} from '@/components/landing/mockups/screen-ui'
import { BLUE_TONE, VIOLET_TONE, photos } from '@/constants/media'

const CYAN_TONE = 'sepia(0.9) hue-rotate(155deg) saturate(2.6) contrast(1.05)'

// Ghost Mode: trims, blend and speed for two overlaid takes.
export const GhostControlsScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 text-white'>
            <MediaTile src={photos.breakdance} position='50% 40%' filter='brightness(0.55) saturate(0.9)' />

            <span className='absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgb(0_0_0/0.45))]' />

            <div className='absolute left-[5.5cqw] top-[2.2cqw] z-10 flex items-center gap-[0.8cqw]'>
                <ScreenButton className='bg-black/50'>
                    <ArrowLeft className='size-[1.5cqw]' />
                    Back
                </ScreenButton>

                <ScreenButton className='bg-black/50'>
                    <Bookmark className='size-[1.5cqw] text-cyan-300' fill='currentColor' />
                </ScreenButton>

                <ScreenButton className='bg-black/50'>
                    Change videos
                </ScreenButton>

                <ScreenButton className='bg-black/50'>
                    <Share2 className='size-[1.5cqw]' />
                </ScreenButton>
            </div>

            <div className='absolute inset-x-[7cqw] bottom-[2.4cqw] top-[7.4cqw] z-10 rounded-[1.6cqw] bg-slate-950/80 p-[2cqw] ring-1 ring-white/10 backdrop-blur-md'>
                <div className='flex items-center justify-between'>
                    <span className='text-[1.8cqw] font-bold tracking-[0.06em]'>
                        OVERLAY
                    </span>

                    <span className='flex gap-[0.8cqw]'>
                        <span className='grid size-[3cqw] place-items-center rounded-[0.7cqw] bg-cyan-400/15 ring-1 ring-cyan-300/40'>
                            <SlidersHorizontal className='size-[1.6cqw] text-cyan-300' />
                        </span>

                        <span className='grid size-[3cqw] place-items-center rounded-[0.7cqw] bg-cyan-400/15 ring-1 ring-cyan-300/40'>
                            <EyeOff className='size-[1.6cqw] text-cyan-300' />
                        </span>
                    </span>
                </div>

                <div className='mt-[1.6cqw] grid grid-cols-2 gap-x-[3cqw] gap-y-[1.6cqw] text-[1.3cqw]'>
                    <div>
                        <div className='flex items-center justify-between'>
                            <span className='font-semibold text-cyan-300'>
                                V1 · <span className='rounded-[0.4cqw] px-[0.4cqw] ring-1 ring-cyan-300/70'>00:01</span> → 00:03
                            </span>

                            <span className='flex gap-[0.5cqw]'>
                                <ScreenButton>−1F</ScreenButton>
                                <ScreenButton>+1F</ScreenButton>
                            </span>
                        </div>

                        <RangeTrack from={22} to={46} color='bg-cyan-300' className='mt-[1.2cqw]' />
                    </div>

                    <div>
                        <div className='flex items-center justify-between'>
                            <span className='font-semibold'>
                                Blend <span className='font-normal text-white/55'>V1 62% · V2 38%</span>
                            </span>
                        </div>

                        <SliderTrack value={62} fill='bg-linear-to-r from-violet-400 to-cyan-300' className='mt-[1.95cqw]' />
                    </div>

                    <div>
                        <div className='flex items-center justify-between'>
                            <span className='font-semibold text-violet-300'>
                                V2 · 00:01 → 00:03
                            </span>

                            <span className='flex gap-[0.5cqw]'>
                                <ScreenButton>−1F</ScreenButton>
                                <ScreenButton>+1F</ScreenButton>
                            </span>
                        </div>

                        <RangeTrack from={18} to={38} color='bg-violet-400' className='mt-[1.2cqw]' />
                    </div>

                    <div>
                        <div className='flex items-center justify-between'>
                            <span className='font-mono text-white/80'>
                                00:00 / 00:01
                            </span>

                            <span className='flex gap-[0.5cqw]'>
                                <ScreenButton>−1F</ScreenButton>
                                <ScreenButton>+1F</ScreenButton>
                            </span>
                        </div>

                        <SliderTrack value={46} fill='bg-cyan-300' className='mt-[1.2cqw]' />
                    </div>
                </div>

                <div className='mt-[1.8cqw] text-[1.3cqw]'>
                    <div className='flex items-center justify-between'>
                        <span className='font-semibold'>
                            Speed <span className='text-cyan-300'>1x</span>
                        </span>

                        <ScreenButton>1x</ScreenButton>
                    </div>

                    <SliderTrack value={58} ticks={5} fill='bg-cyan-300' className='mt-[1.2cqw]' />
                </div>

                <div className='mt-[1.6cqw] flex items-center justify-center gap-[5cqw]'>
                    <RotateCcw className='size-[2cqw] text-white/80' />

                    <span className='grid size-[3.6cqw] place-items-center rounded-full bg-cyan-300 text-slate-950'>
                        <Play className='size-[1.8cqw]' fill='currentColor' />
                    </span>

                    <RotateCw className='size-[2cqw] text-white/80' />
                </div>
            </div>
        </div>
    )
}

// Ghost Mode in action: two tinted takes blended into one view, controls hidden.
export const GhostOverlayScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 bg-white'>
            <MediaTile src={photos.breakdance} position='46% 40%' filter={CYAN_TONE} className='bg-white' />

            <MediaTile
                src={photos.breakdance}
                position='54% 40%'
                mirror
                filter={VIOLET_TONE}
                className='bg-white opacity-90 mix-blend-multiply'
            />

            <ScreenChip className='left-[5cqw] top-[2.6cqw]' icon={<Ghost className='size-[1.6cqw]' />}>
                Ghost · Blend 50%
            </ScreenChip>

            <span className='absolute bottom-[2.4cqw] right-[3cqw] z-10 flex gap-[1cqw]'>
                <span className='inline-flex items-center gap-[0.6cqw] rounded-full bg-black/55 px-[1.2cqw] py-[0.55cqw] text-[1.4cqw] font-semibold leading-none text-white'>
                    <span className='size-[1.1cqw] rounded-full bg-cyan-300' />
                    V1
                </span>

                <span className='inline-flex items-center gap-[0.6cqw] rounded-full bg-black/55 px-[1.2cqw] py-[0.55cqw] text-[1.4cqw] font-semibold leading-none text-white'>
                    <span className='size-[1.1cqw] rounded-full bg-violet-400' />
                    V2
                </span>
            </span>
        </div>
    )
}

// Zoom per video: the full shot with its focus area, next to the zoomed view of that area.
export const ZoomScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 flex gap-[0.5cqw]'>
            <div className='relative h-full flex-1 overflow-hidden'>
                <MediaTile src={photos.eagle} position='80% 50%' />

                <span className='absolute left-[44%] top-[16%] z-10 h-[44%] w-[44%] rounded-[0.8cqw] border-[0.3cqw] border-dashed border-white shadow-[0_0_0_100cqw_rgb(0_0_0/0.3)]' />

                <ScreenChip className='bottom-[2.4cqw] left-[5cqw]'>
                    1.0x
                </ScreenChip>
            </div>

            <div className='relative h-full flex-1 overflow-hidden'>
                <div className='absolute inset-0 origin-top-left' style={{ transform: 'scale(2.2727) translate(-44%, -16%)' }}>
                    <MediaTile src={photos.eagle} position='80% 50%' />
                </div>

                <ScreenChip className='right-[2.6cqw] top-[2.6cqw]' tone='cyan' icon={<ZoomIn className='size-[1.6cqw]' />}>
                    2.3x
                </ScreenChip>
            </div>
        </div>
    )
}

const QUADRANTS: { look: TileLook, badge: string, label: string, muted?: boolean }[] = [
    { look: { src: photos.waterfall, position: '40% 45%' }, badge: 'bg-sky-500', label: 'Q1' },
    { look: { src: photos.dancer, position: '45% 55%' }, badge: 'bg-emerald-500', label: 'Q2' },
    { look: { src: photos.sportsCar, position: '50% 58%' }, badge: 'bg-amber-500', label: 'Q3' },
    { look: { src: photos.parkour, position: '60% 40%' }, badge: 'bg-violet-500', label: 'Q4', muted: true }
]

const LEVELS = [
    { label: 'Q1', value: 100, fill: 'bg-sky-400', badge: 'bg-sky-500' },
    { label: 'Q2', value: 80, fill: 'bg-emerald-400', badge: 'bg-emerald-500' },
    { label: 'Q3', value: 55, fill: 'bg-amber-400', badge: 'bg-amber-500' },
    { label: 'Q4', value: 0, fill: 'bg-violet-400', badge: 'bg-violet-500' }
]

// Independent audio: one volume and mute switch per quadrant.
export const AudioScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 text-white'>
            <div className='absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[0.4cqw]'>
                {QUADRANTS.map((quadrant) => (
                    <div key={quadrant.label} className='relative overflow-hidden'>
                        <MediaTile {...quadrant.look} />

                        <span className={cn('absolute right-[1.6cqw] top-[1.6cqw] z-10 inline-flex items-center gap-[0.4cqw] rounded-full px-[0.9cqw] py-[0.4cqw] text-[1.2cqw] font-bold leading-none', quadrant.badge)}>
                            {quadrant.muted ? <VolumeX className='size-[1.3cqw]' /> : <Volume2 className='size-[1.3cqw]' />}
                            {quadrant.label}
                        </span>
                    </div>
                ))}
            </div>

            <span className='absolute inset-0 bg-black/35' />

            <div className='absolute left-1/2 top-1/2 z-10 w-[48cqw] -translate-x-1/2 -translate-y-1/2 rounded-[1.6cqw] bg-slate-950/85 p-[2cqw] ring-1 ring-white/10 backdrop-blur-md'>
                <div className='flex items-center justify-between'>
                    <span className='flex items-center gap-[1cqw]'>
                        <span className='grid size-[3.4cqw] place-items-center rounded-full bg-cyan-400/15 ring-1 ring-cyan-300/40'>
                            <Volume2 className='size-[1.8cqw] text-cyan-300' />
                        </span>

                        <span className='text-[2cqw] font-semibold'>
                            Audio settings
                        </span>
                    </span>

                    <span className='grid size-[3cqw] place-items-center rounded-full bg-white/10'>
                        <X className='size-[1.6cqw] text-white/80' />
                    </span>
                </div>

                <p className='mt-[1cqw] text-[1.15cqw] font-semibold uppercase tracking-[0.12em] text-white/55'>
                    Volume by quadrant
                </p>

                <ul className='mt-[1.4cqw] space-y-[1.1cqw]'>
                    {LEVELS.map((level) => {
                        const on = level.value > 0

                        return (
                            <li key={level.label} className='flex items-center gap-[1.2cqw]'>
                                <span className={cn('grid size-[2.6cqw] shrink-0 place-items-center rounded-full text-[1.1cqw] font-bold', level.badge)}>
                                    {level.label}
                                </span>

                                <span className='relative h-[0.9cqw] flex-1 rounded-full bg-white/10'>
                                    <span className={cn('absolute inset-y-0 left-0 rounded-full', level.fill)} style={{ width: `${level.value}%` }} />
                                    <span className='absolute top-1/2 h-[2.2cqw] w-[0.6cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white' style={{ left: `${level.value}%` }} />
                                </span>

                                <span className='w-[5cqw] text-right text-[1.2cqw] text-white/80'>
                                    {on ? `${level.value}%` : 'Muted'}
                                </span>

                                <span className={cn('w-[3.6cqw] rounded-full py-[0.35cqw] text-center text-[1.05cqw] font-bold', on ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/50')}>
                                    {on ? 'On' : 'Off'}
                                </span>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

const FILTERS: { name: string, filter?: string }[] = [
    { name: 'Original' },
    { name: 'Violet', filter: 'hue-rotate(255deg) saturate(2.2)' },
    { name: 'Azure', filter: 'hue-rotate(170deg) saturate(2)' },
    { name: 'Noir', filter: 'grayscale(1) contrast(1.25)' }
]

const SWATCHES = [
    'bg-linear-to-br from-orange-300 to-slate-200',
    'bg-linear-to-br from-fuchsia-400 to-violet-600',
    'bg-linear-to-br from-sky-300 to-blue-600',
    'bg-linear-to-br from-zinc-200 to-zinc-800',
    'bg-linear-to-br from-amber-300 to-rose-500'
]

// Filters: the same shot with four different looks.
export const FiltersScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0'>
            <div className='absolute inset-0 grid grid-cols-2 grid-rows-2 gap-[0.4cqw]'>
                {FILTERS.map((item, index) => (
                    <div key={item.name} className='relative overflow-hidden'>
                        <MediaTile src={photos.sportsCar} position='50% 58%' filter={item.filter} />

                        <ScreenChip className={cn('top-[2cqw]', index % 2 === 0 ? 'left-[5cqw]' : 'left-[2cqw]')}>
                            {item.name}
                        </ScreenChip>
                    </div>
                ))}
            </div>

            <span className='absolute bottom-[2.2cqw] left-1/2 z-10 flex -translate-x-1/2 items-center gap-[1cqw] rounded-full bg-black/60 px-[1.4cqw] py-[0.9cqw] ring-1 ring-white/15 backdrop-blur-sm'>
                {SWATCHES.map((swatch, index) => (
                    <span
                        key={swatch}
                        className={cn('size-[2.6cqw] rounded-full', swatch, index === 1 && 'ring-[0.3cqw] ring-white ring-offset-[0.25cqw] ring-offset-black')}
                    />
                ))}
            </span>
        </div>
    )
}

const FRAMES: { look: TileLook, frame: string }[] = [
    {
        look: { src: photos.surf, position: '62% 48%' },
        frame: 'bg-[linear-gradient(140deg,#f472b6,#a855f7_50%,#22d3ee)] shadow-[0_0_2.4cqw_rgb(236_72_153/0.45)]'
    },
    {
        look: { src: photos.concert, position: '45% 50%' },
        frame: 'bg-[linear-gradient(140deg,#fde047,#22c55e_50%,#06b6d4)] shadow-[0_0_2.4cqw_rgb(34_197_94/0.4)]'
    },
    {
        look: { src: photos.eagle, position: '72% 40%' },
        frame: 'bg-[linear-gradient(140deg,#38bdf8,#6366f1_50%,#ec4899)] shadow-[0_0_2.4cqw_rgb(99_102_241/0.45)]'
    }
]

// Frames and borders: three views, each with its own glowing frame.
export const FramesScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 bg-[radial-gradient(120%_100%_at_50%_0%,#1e1b4b,#05070d_70%)]'>
            <div className='absolute inset-y-[3.2cqw] left-[8cqw] right-[7cqw] flex gap-[2.4cqw]'>
                {FRAMES.map((item) => (
                    <div key={item.look.src} className={cn('relative flex-1 rounded-[1.6cqw] p-[0.55cqw]', item.frame)}>
                        <div className='relative h-full w-full overflow-hidden rounded-[1.1cqw]'>
                            <MediaTile {...item.look} />
                        </div>
                    </div>
                ))}
            </div>

            <span className='absolute left-[4.2cqw] top-1/2 z-10 grid size-[3cqw] -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white/80'>
                <ChevronLeft className='size-[1.8cqw]' />
            </span>

            <span className='absolute right-[2.2cqw] top-1/2 z-10 grid size-[3cqw] -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white/80'>
                <ChevronRight className='size-[1.8cqw]' />
            </span>
        </div>
    )
}

const PLAYLIST: { title: string, look: TileLook }[] = [
    { title: 'Breakdance freeze', look: { src: photos.breakdance, position: '50% 40%' } },
    { title: 'Neon city run', look: { src: photos.neonCity, position: '70% 30%' } },
    { title: 'Light trails', look: { src: photos.concert, position: '45% 50%' } },
    { title: 'Drone · Waterfall', look: { src: photos.waterfall, position: '40% 45%' } },
    { title: 'Ocean wave', look: { src: photos.surf, position: '62% 48%' } }
]

const SORT_OPTIONS = [
    { label: 'A-Z', icon: <ArrowDownAZ className='size-[1.2cqw]' /> },
    { label: 'Z-A', icon: <ArrowUpZA className='size-[1.2cqw]' /> },
    { label: 'Shuffle', icon: <Shuffle className='size-[1.2cqw]' /> },
    { label: 'Invert', icon: <ArrowDownUp className='size-[1.2cqw]' /> }
]

// Playlist manager: clips, their order and a preview.
export const PlaylistScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 flex flex-col bg-[linear-gradient(180deg,#0d1424,#070b14)] py-[2.4cqw] pl-[5.5cqw] pr-[3cqw] text-white'>
            <div className='flex items-center justify-between gap-[1cqw]'>
                <div className='flex items-center gap-[1.2cqw]'>
                    <span className='grid size-[3.4cqw] place-items-center rounded-full bg-white/10'>
                        <ArrowLeft className='size-[1.8cqw]' />
                    </span>

                    <span className='grid size-[3.4cqw] place-items-center rounded-[0.8cqw] bg-violet-500/20 ring-1 ring-violet-300/40'>
                        <ListVideo className='size-[1.9cqw] text-violet-200' />
                    </span>

                    <span className='leading-tight'>
                        <span className='block text-[1.9cqw] font-bold'>
                            Playlist manager
                        </span>

                        <span className='block text-[1.2cqw] text-white/55'>
                            5 playlists · 14 videos available
                        </span>
                    </span>
                </div>

                <div className='flex items-center gap-[0.8cqw]'>
                    <ScreenButton>
                        Select playlists
                        <ChevronDown className='size-[1.3cqw]' />
                    </ScreenButton>

                    <span className='inline-flex items-center gap-[0.5cqw] rounded-[0.8cqw] bg-emerald-500 px-[1cqw] py-[0.55cqw] text-[1.25cqw] font-bold leading-none text-emerald-950'>
                        <Plus className='size-[1.3cqw]' />
                        New playlist
                    </span>

                    <span className='grid size-[3cqw] place-items-center rounded-full bg-white/10'>
                        <X className='size-[1.6cqw] text-white/80' />
                    </span>
                </div>
            </div>

            <div className='mt-[1.8cqw] flex min-h-0 flex-1 gap-[1.6cqw]'>
                <div className='relative w-[36%] overflow-hidden rounded-[1.2cqw] ring-1 ring-white/10'>
                    <MediaTile src={photos.breakdance} position='50% 40%' />

                    <div className='absolute inset-x-0 bottom-0 bg-linear-to-t from-black/85 to-transparent px-[1.2cqw] pb-[1.1cqw] pt-[4cqw]'>
                        <div className='flex items-center gap-[0.8cqw] font-mono text-[1.2cqw]'>
                            <span className='grid size-[2.4cqw] place-items-center rounded-full bg-cyan-300 text-slate-950'>
                                <Pause className='size-[1.2cqw]' fill='currentColor' />
                            </span>
                            00:00 / 00:08
                        </div>

                        <span className='mt-[0.9cqw] block h-[0.5cqw] rounded-full bg-white/25'>
                            <span className='block h-full w-[35%] rounded-full bg-cyan-300' />
                        </span>
                    </div>
                </div>

                <div className='flex min-w-0 flex-1 flex-col rounded-[1.2cqw] bg-white/[0.04] p-[1.4cqw] ring-1 ring-white/10'>
                    <div className='flex items-center justify-between'>
                        <span className='leading-tight'>
                            <span className='flex items-center gap-[0.6cqw] text-[1.6cqw] font-bold'>
                                Demo videos
                                <Pencil className='size-[1.3cqw] text-white/50' />
                            </span>

                            <span className='block text-[1.15cqw] text-white/50'>
                                14/14 active videos
                            </span>
                        </span>

                        <span className='flex items-center gap-[0.6cqw]'>
                            <span className='inline-flex items-center gap-[0.4cqw] rounded-[0.7cqw] bg-emerald-500 px-[0.9cqw] py-[0.5cqw] text-[1.15cqw] font-bold leading-none text-emerald-950'>
                                <Plus className='size-[1.2cqw]' />
                                Add
                            </span>

                            <span className='grid size-[2.5cqw] place-items-center rounded-[0.7cqw] bg-rose-500/20 text-rose-300'>
                                <Trash2 className='size-[1.3cqw]' />
                            </span>
                        </span>
                    </div>

                    <div className='mt-[1cqw] flex flex-wrap items-center gap-[0.6cqw] text-[1.1cqw] text-white/60'>
                        Sort:
                        {SORT_OPTIONS.map((option) => (
                            <span key={option.label} className='inline-flex items-center gap-[0.4cqw] rounded-[0.6cqw] bg-white/8 px-[0.8cqw] py-[0.45cqw] leading-none text-white/85'>
                                {option.icon}
                                {option.label}
                            </span>
                        ))}
                    </div>

                    <ul className='mt-[1cqw] space-y-[0.6cqw]'>
                        {PLAYLIST.map((item, index) => (
                            <li
                                key={item.title}
                                className={cn(
                                    'flex items-center gap-[1cqw] rounded-[0.7cqw] px-[0.6cqw] py-[0.35cqw]',
                                    index === 0 ? 'bg-cyan-400/10 text-cyan-200 ring-1 ring-cyan-300/40' : 'text-white/85'
                                )}
                            >
                                <span className='w-[1.4cqw] text-[1.1cqw] text-white/45'>
                                    {index + 1}
                                </span>

                                <span className='relative h-[2.7cqw] w-[4.8cqw] shrink-0 overflow-hidden rounded-[0.4cqw]'>
                                    <MediaTile {...item.look} />
                                </span>

                                <span className='min-w-0 flex-1 truncate text-[1.2cqw] font-medium'>
                                    {item.title}
                                </span>

                                <span className='flex items-center gap-[0.8cqw] text-white/45'>
                                    <GripVertical className='size-[1.3cqw]' />
                                    <Eye className='size-[1.3cqw]' />
                                    <Trash2 className='size-[1.3cqw]' />
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    )
}

const WALL_MENU = [
    { label: 'Grid', icon: LayoutGrid, active: true },
    { label: 'Playlists', icon: ListVideo },
    { label: 'Sessions', icon: Layers },
    { label: 'Pause', icon: Pause },
    { label: 'Mute', icon: VolumeX }
]

// Video wall simulator: one shot spread across six screens.
export const VideoWallScreen: FC = (): ReactNode => {
    return (
        <div className='absolute inset-0 flex bg-[#05070d] text-white'>
            <div className='relative my-[2.4cqw] ml-[5.5cqw] mr-[1.6cqw] flex-1 overflow-hidden rounded-[0.6cqw]'>
                <MediaTile src={photos.surf} position='60% 45%' filter={BLUE_TONE} />

                <div className='absolute inset-0 grid grid-cols-3 grid-rows-2'>
                    {Array.from({ length: 6 }, (_, index) => (
                        <span
                            key={index}
                            className={cn(
                                'border-[0.45cqw] border-[#05070d] bg-[radial-gradient(120%_90%_at_50%_40%,transparent_55%,rgb(0_0_0/0.35))]',
                                index % 2 === 1 && 'backdrop-brightness-110'
                            )}
                        />
                    ))}
                </div>
            </div>

            <div className='my-[2.4cqw] mr-[3cqw] flex w-[8.5cqw] flex-col items-center justify-between rounded-[1.2cqw] bg-white/[0.05] py-[1.4cqw] ring-1 ring-white/10'>
                <span className='grid size-[3.6cqw] place-items-center rounded-full ring-[0.25cqw] ring-amber-400'>
                    <User className='size-[1.8cqw] text-amber-300' />
                </span>

                {WALL_MENU.map((item) => {
                    const Icon = item.icon

                    return (
                        <span
                            key={item.label}
                            className={cn('flex flex-col items-center gap-[0.3cqw] text-[1cqw] leading-none', item.active ? 'text-cyan-300' : 'text-white/70')}
                        >
                            <Icon className='size-[1.9cqw]' />
                            {item.label}
                        </span>
                    )
                })}

                <span className='rounded-full bg-amber-400 px-[0.9cqw] py-[0.35cqw] text-[1cqw] font-extrabold leading-none text-amber-950'>
                    PRO
                </span>
            </div>
        </div>
    )
}
